import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  FiAlertTriangle,
  FiCalendar,
  FiFileText,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";
import { LuIndianRupee } from "react-icons/lu";

type PayoutCancelScheduledTransferModalProps = {
  transactionId: string;
  beneficiary: string;
  amount: string;
  scheduledFor: string;
  confirming?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

function PayoutCancelScheduledTransferModal({
  transactionId,
  beneficiary,
  amount,
  scheduledFor,
  confirming = false,
  onClose,
  onConfirm,
}: PayoutCancelScheduledTransferModalProps) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-[#1B163B]/45 px-4 py-8 backdrop-blur-[2px]">
      <button
        type="button"
        aria-label="Close cancel scheduled transfer dialog"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <section className="relative z-10 w-full max-w-[720px] rounded-[14px] border border-[#E0E5F2] bg-white p-7 shadow-[0_24px_70px_rgba(27,22,59,0.22)]">
        <div className="mb-7 flex items-start justify-between gap-5">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#FEF2F2] text-xl text-[#DC2626]">
              <FiAlertTriangle />
            </span>
            <h2 className="text-[24px] font-semibold leading-8 text-[#1B163B]">
              Cancel Scheduled Transfer
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] text-xl text-[#1B163B] hover:bg-[#F4F7FE]"
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        <div className="grid gap-5 rounded-[8px] bg-white sm:grid-cols-2">
          <div className="flex items-center gap-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EEF2FF] text-lg text-[#6687FF]">
              <FiFileText />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#505E8E]">Transaction ID</p>
              <p className="mt-1 break-all text-sm font-semibold text-[#1B163B]">
                {transactionId || "-"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 border-[#E0E5F2] sm:border-l sm:pl-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EEF2FF] text-lg text-[#6687FF]">
              <FiUser />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#505E8E]">Beneficiary</p>
              <p className="mt-1 truncate text-sm font-semibold text-[#1B163B]">
                {beneficiary || "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-7 flex gap-4 rounded-[8px] border border-[#FCA5A5] bg-[#FFF7F7] px-5 py-4">
          <FiAlertTriangle className="mt-1 shrink-0 text-lg text-[#DC2626]" />
          <div className="text-sm font-medium leading-6 text-[#1B163B]">
            <p>
              Cancelling will remove this scheduled transfer from the current
              transfers list.
            </p>
            <p className="font-semibold text-[#DC2626]">
              This action cannot be undone.
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-[8px] border border-[#E0E5F2] p-5">
          <h3 className="mb-5 text-sm font-semibold text-[#1B163B]">
            Transfer Details
          </h3>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EEF2FF] text-lg text-[#6687FF]">
                <LuIndianRupee />
              </span>
              <div>
                <p className="text-xs font-medium text-[#505E8E]">Amount</p>
                <p className="mt-1 text-sm font-semibold text-[#1B163B]">
                  {amount || "-"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 border-[#E0E5F2] sm:border-l sm:pl-6">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EEF2FF] text-lg text-[#6687FF]">
                <FiCalendar />
              </span>
              <div>
                <p className="text-xs font-medium text-[#505E8E]">
                  Scheduled For
                </p>
                <p className="mt-1 text-sm font-semibold text-[#1B163B]">
                  {scheduledFor || "-"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-col-reverse gap-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="h-12 rounded-[6px] border border-[#6687FF] bg-white px-8 text-sm font-semibold text-[#2F55D4] disabled:opacity-50 sm:min-w-[200px]"
          >
            Keep Transfer
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming}
            style={{ backgroundColor: "#D51F35", color: "#FFFFFF" }}
            className="flex h-12 items-center justify-center gap-3 rounded-[6px] bg-[#D51F35] px-8 text-sm font-semibold text-white shadow-sm hover:bg-[#BE172C] disabled:opacity-50 sm:min-w-[260px]"
          >
            <FiTrash2 className="text-lg" />
            {confirming ? "Cancelling..." : "Cancel Scheduled Transfer"}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

export default PayoutCancelScheduledTransferModal;
